import React from 'react';
import HomeLink from './HomeLink';
import UsdApprox from '../../../components/UsdApprox';
import { getCardThumbnailUrl } from '../../../utils/imageUrl';
import { formatKesAmount } from '../../../utils/formatCurrency';
import { formatSyndicatedBeds } from '../../../lib/api/syndicatedQuery';
import { AreaIcon, BathIcon, BedIcon, PinIcon } from './icons';
import {
  AmenityTag,
  FreshnessBadge,
  PhotoCountBadge,
  SaveHeartButton,
  SpotlightBadge,
  VerifiedAgentBadge,
} from './Badges';
import { CARD_HOVER, DISPLAY_FONT, FOCUS_RING } from './tokens';
import { savedTargetFromView, type HomeListingView } from './listingView';
import type { SavedTarget } from '../../../lib/api/savedTargets';

/**
 * HOME-01 — the redesign's listing card, one component with size variants.
 *
 * VARIANTS
 *   spotlight    the paid carousel. 312px card / 196px image on desktop, 250 /
 *                150 on mobile, and the only variant carrying the Spotlight badge.
 *   compact      "New this week". 264 / 164 on desktop, and a freshness tag
 *                where spotlight has its badge.
 *   grid         All properties, desktop. Fills its grid cell.
 *   mobile-list  All properties in List mode. VERTICAL on a phone — image
 *                across the card, facts under it, per the design's `mobileList`
 *                — and horizontal from `sm` up, where the toggle also offers it.
 *
 * FIX ROUND 1 — WHY grid AND mobile-list NOW EXIST
 * They were held back in the first build because "All properties" renders two
 * kinds of row and `SyndicatedListingCard` deliberately MIRRORS whichever
 * marketplace card the view is showing (SYND-06) — so restyling only the
 * marketplace half would have broken the mirror. Dirie's call on 2026-08-13 was
 * to move BOTH halves, so the mirror is preserved by this card rendering both:
 * `source: 'syndicated'` rows come through the same component, wearing the same
 * shell, and every SYND-06/11 rule they carry is enforced here rather than
 * abandoned —
 *
 *   * the destination stays `/syndicated/:id` (set by the mapper, not here);
 *   * "Listed by {source}" is unconditional — naming the company selling it is
 *     policy, and this card cannot render an indexed row without it;
 *   * no Verified badge, no Spotlight badge, no message route. /syndication
 *     promises that in writing and the outreach email quotes the page;
 *   * THE SAVE HEART IS ON BOTH KINDS AS OF SYND-15, and only because the
 *     database changed underneath it. It used to be omitted here — not
 *     disabled, because a greyed heart is a promise with a date on it — since
 *     `saved_properties.listing_id` REFERENCED `listings(id)` and a syndicated
 *     uuid in that column came back 23503. `saved_properties` is polymorphic
 *     now (migration 20260820100000): one row, one target, `listing_id` XOR
 *     `syndicated_id`. A save is a private bookmark — not a contact route and
 *     not a verification claim — so it touches neither promise above;
 *   * SYND-11's price line is composed by `syndicatedPriceLine` in the mapper,
 *     so the published figure stays the figure and our KES arithmetic stays
 *     fine print beneath it. This card prints what it is handed.
 *
 * ONE CURRENCY FORMAT (supersedes HOME-06). Every price on every variant is
 * `HomeListingView.price.label`, and for marketplace rows the mapper builds
 * that with CURR-03's `formatListingPrice` — "KES", never "Ksh". The old cards
 * reached for `formatCurrency` and the same screen printed both spellings.
 *
 * ADAPTIVE FACTS. A card shows the facts its property type actually has. A plot
 * has no bedrooms and no bathrooms, and "0 beds" on land is worse than no line;
 * an indexed row has neither a bath count nor a floor area, because the columns
 * do not exist. See `metaParts` below — nothing there can render "undefined".
 *
 * LINKING
 * The anchor is stretched across the card instead of wrapping it, so the save
 * button is a sibling rather than a `<button>` inside an `<a>`. Nested
 * interactive content is invalid HTML and relies on every click handler
 * remembering to `preventDefault`; a stretched link cannot be got wrong by
 * forgetting something.
 */

export type ListingCardVariant = 'spotlight' | 'compact' | 'grid' | 'mobile-list';

interface ListingCardBaseProps {
  listing: HomeListingView;
  saved: boolean;
  /**
   * SYND-15 — the handler is handed a `SavedTarget`, not a bare uuid.
   *
   * The card is the one place that knows which of the two kinds it is
   * rendering, and it composes the target from `listing.source` via
   * `savedTargetFromView`. A parent that received only an id would have to
   * re-derive the kind from its own copy of the row, which is precisely how a
   * syndicated uuid ends up in `listing_id`.
   */
  onSave: (target: SavedTarget, e: React.MouseEvent) => void;
  /** First card of the first row. Eager + high priority rather than lazy. */
  isLCP?: boolean;
}

/**
 * FIX ROUND 2 — the freshness tag belongs to ONE variant, and the type says so.
 *
 * Every All-properties card was rendering a two-line pill over its photograph:
 * "Listed 132 days ago". The design has no freshness badge on those cards at
 * all — it puts one on the "New this week" carousel, where every row is seven
 * days old or less by construction and the tag is therefore the point of the
 * row rather than a fact about one card.
 *
 * On the main list it did the opposite of what a freshness tag is for: it
 * turned the highest-traffic page in the product into an inventory-age report,
 * and told every buyer, on every card, how long nobody had bought it.
 *
 * Removed from the render, not hidden — and removed from the TYPE, so it is a
 * compile error to pass one to a grid or list card rather than a thing a
 * future caller has to remember not to do.
 */
type ListingCardProps = ListingCardBaseProps &
  (
    | {
        variant: 'compact';
        /** "Listed 2 days ago" / "Added 2 days ago". Null → no tag. */
        freshLabel?: string | null;
      }
    | {
        variant: 'spotlight' | 'grid' | 'mobile-list';
        freshLabel?: never;
      }
  );

/** m² per acre. Kenyan land is quoted in acres; the column is in m². */
const SQM_PER_ACRE = 4046.8564224;

/**
 * "0.25 acres" beside the m² figure, for land big enough that acres is how
 * anyone would actually say it. Below a quarter acre the conversion produces
 * "0.08 acres", which is a number nobody uses and two decimal places of false
 * precision, so the m² figure stands alone.
 */
function acresLabel(areaSqm: number): string | null {
  const acres = areaSqm / SQM_PER_ACRE;
  if (acres < 0.25) return null;
  const rounded = acres >= 10 ? Math.round(acres) : Math.round(acres * 100) / 100;
  return `${rounded} ${rounded === 1 ? 'acre' : 'acres'}`;
}

/** "3 beds" / "1–4 beds" when a development announced a range. */
function bedsLabel(beds: number | null, bedsMax: number | null): string | null {
  if (!beds) return null;
  if (bedsMax && bedsMax > beds) return `${beds}–${bedsMax} beds`;
  return `${beds} ${beds === 1 ? 'bed' : 'beds'}`;
}

interface MetaPart {
  key: string;
  icon: React.ReactNode;
  text: string;
}

/**
 * The meta row, by property type. Verified columns only — nothing here reads a
 * field that does not exist on the row it is given.
 *
 *   syndicated       beds range only. `syndicated_public` has NO baths and NO
 *                    area_sqm, so those slots are not "empty", they are absent.
 *                    The neighbourhood is rendered separately by every variant.
 *   plot             area (and acres where the number is one anyone would say),
 *                    plus the title-deed class when set. Beds and baths are
 *                    suppressed ENTIRELY — not zeroed.
 *   new_development  a bed RANGE when one was announced, because a development
 *                    is a set of homes rather than one.
 *   apartment        area · beds · baths, with the service charge on its own
 *                    line below (it is a recurring cost, not a dimension).
 *   villa/bungalow/
 *   townhouse        area · beds · baths.
 */
function metaParts(listing: HomeListingView): MetaPart[] {
  const parts: MetaPart[] = [];
  const iconStroke = '#666666';

  if (listing.source === 'syndicated') {
    const label = formatSyndicatedBeds({
      beds_min: listing.beds,
      beds_max: listing.bedsMax,
    });
    if (label) {
      parts.push({ key: 'beds', icon: <BedIcon size={13} stroke={iconStroke} />, text: label });
    }
    return parts;
  }

  const type = listing.propertyType;

  if (listing.areaSqm) {
    const acres = type === 'plot' ? acresLabel(listing.areaSqm) : null;
    parts.push({
      key: 'area',
      icon: <AreaIcon size={13} stroke={iconStroke} />,
      text: acres
        ? `${listing.areaSqm.toLocaleString('en-US')} m² · ${acres}`
        : `${listing.areaSqm.toLocaleString('en-US')} m²`,
    });
  }

  if (type === 'plot') {
    // Land: no bed or bath icons at all, and the deed class where a house
    // would carry a bathroom count — it is the fact a plot buyer is looking
    // for, and the one they most often cannot find.
    if (listing.titleDeedType) {
      parts.push({
        key: 'deed',
        icon: null,
        text: `${listing.titleDeedType.replace(/_/g, ' ')} title`,
      });
    }
    return parts;
  }

  const beds = bedsLabel(listing.beds, type === 'new_development' ? listing.bedsMax : null);
  if (beds) {
    parts.push({ key: 'beds', icon: <BedIcon size={13} stroke={iconStroke} />, text: beds });
  }

  if (listing.baths) {
    parts.push({
      key: 'baths',
      icon: <BathIcon size={13} stroke={iconStroke} />,
      text: `${listing.baths} ${listing.baths === 1 ? 'bath' : 'baths'}`,
    });
  }

  return parts;
}

/**
 * Up to three amenity tags, chosen for the property type.
 *
 * A pool on a plot of land is not a fact about the plot, and a gym is not what
 * anyone is checking before buying land — so land shows the two facts that
 * decide a land purchase (is it beaconed, is the survey approved) and homes
 * show the amenities that differentiate them. An indexed row shows SYND-06's
 * hand-authored tags and nothing inferred.
 *
 * Returns [] rather than a filler tag: the caller renders no row at all for an
 * empty result, because an empty tag row is a visible gap that reads as a
 * loading failure.
 */
function amenityTags(listing: HomeListingView): string[] {
  if (listing.source === 'syndicated') return listing.syndicatedAmenities.slice(0, 3);

  const tags: string[] = [];

  if (listing.propertyType === 'plot') {
    if (listing.beaconed) tags.push('Beaconed');
    if (listing.surveyApproved) tags.push('Survey approved');
    if (listing.gatedCommunity) tags.push('Gated');
    return tags.slice(0, 3);
  }

  if (listing.pool) tags.push('Pool');
  if (listing.gym) tags.push('Gym');
  if (listing.backupGenerator) tags.push('Backup generator');
  if (tags.length < 3 && listing.gatedCommunity) tags.push('Gated');
  return tags.slice(0, 3);
}

/** "Service charge KES 18,000/mo" — apartments, when the column is set. */
function serviceChargeLine(listing: HomeListingView): string | null {
  if (listing.source !== 'listing') return null;
  if (listing.propertyType !== 'apartment') return null;
  if (!listing.serviceCharge) return null;
  return `Service charge KES ${formatKesAmount(listing.serviceCharge)}/mo`;
}

/** The price block, shared by every variant. */
const PriceBlock: React.FC<{ listing: HomeListingView; className: string }> = ({
  listing,
  className,
}) => (
  <>
    <div
      className={`font-extrabold tracking-[-0.01em] ${className} ${
        listing.price.announced ? 'text-[#134025]' : 'text-[#666666]'
      }`}
      style={{ fontFamily: DISPLAY_FONT }}
    >
      {listing.price.label}
    </div>

    {/* CURR-01/02: renders nothing unless the amount is genuinely in KES, which
        is how the buyer toggle stands itself down on foreign-currency rows. */}
    {listing.price.usdToggleKES !== null && (
      <UsdApprox
        amountKES={listing.price.usdToggleKES}
        maxAmountKES={listing.price.usdToggleMaxKES ?? undefined}
        currency="KES"
        variant="card"
      />
    )}

    {/* SYND-11: our KES arithmetic beside a published foreign price, dressed as
        the fine print it is. Wraps rather than truncates — the sentence names
        both currencies and which one the seller published. */}
    {listing.price.kesApprox && (
      <p className="mt-1 text-[11.5px] font-normal leading-snug text-[#666666]">
        {listing.price.kesApprox}
      </p>
    )}
  </>
);

/** The facts row + service charge + amenity tags, shared by every variant. */
const FactsBlock: React.FC<{
  listing: HomeListingView;
  textClass: string;
  gapClass: string;
  showTags: boolean;
}> = ({ listing, textClass, gapClass, showTags }) => {
  const parts = metaParts(listing);
  const service = serviceChargeLine(listing);
  const tags = showTags ? amenityTags(listing) : [];

  return (
    <>
      {parts.length > 0 && (
        <div className={`flex flex-wrap items-center ${gapClass}`}>
          {parts.map((part) => (
            <span
              key={part.key}
              className={`inline-flex items-center gap-1 font-medium text-[#666666] ${textClass}`}
            >
              {part.icon}
              {part.text}
            </span>
          ))}
        </div>
      )}

      {service && <div className={`mt-1 text-[#666666] ${textClass}`}>{service}</div>}

      {/* Never an empty row: no tags → no container. */}
      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <AmenityTag key={tag} label={tag} />
          ))}
        </div>
      )}
    </>
  );
};

/**
 * Attribution.
 *
 * On an indexed row this is REQUIRED and unconditional — SYND-01's promise is
 * that every indexed listing names the company selling it. On a marketplace row
 * it is the agency (falling back to the agent) and may be absent.
 */
const AttributionLine: React.FC<{
  listing: HomeListingView;
  /**
   * The caller owns the display class (`flex`, `hidden lg:flex`, …) because
   * the design hides this line at some breakpoints on some variants, and a
   * component that set `flex` on itself would be fighting the `hidden` its
   * caller passed — with the winner decided by Tailwind's output order rather
   * than by anyone's intent.
   */
  className?: string;
}> = ({ listing, className = 'flex' }) => {
  if (listing.source === 'syndicated') {
    return (
      <div className={`min-h-[20px] items-center ${className}`}>
        <span className="truncate text-[12px] text-[#666666]">Listed by {listing.agentLabel}</span>
      </div>
    );
  }
  if (!listing.agentLabel && !listing.verified) return null;
  return (
    <div className={`min-h-[20px] items-center gap-1.5 ${className}`}>
      <VerifiedAgentBadge verified={listing.verified} />
      {listing.agentLabel && (
        <span className="ml-auto truncate text-[12px] text-[#666666]">{listing.agentLabel}</span>
      )}
    </div>
  );
};

/**
 * The photograph, plus the credit an indexed photo may never be shown without.
 * A credit that scrolls away from the picture it credits is not a credit, so it
 * sits over the image (SYND-02).
 */
const CardPhoto: React.FC<{ listing: HomeListingView; sizes: string; isLCP: boolean }> = ({
  listing,
  sizes,
  isLCP,
}) => (
  <>
    <img
      src={getCardThumbnailUrl(listing.imageUrl)}
      alt=""
      loading={isLCP ? 'eager' : 'lazy'}
      fetchPriority={isLCP ? 'high' : undefined}
      decoding={isLCP ? 'sync' : 'async'}
      sizes={sizes}
      className="h-full w-full object-cover"
    />
    {listing.photoCredit && (
      <span className="pointer-events-none absolute bottom-0 left-0 right-0 bg-black/55 px-2 py-1 text-[10px] leading-tight text-white">
        Photo: {listing.photoCredit}
      </span>
    )}
  </>
);

const ListingCard: React.FC<ListingCardProps> = (props) => {
  const { listing, variant, saved, onSave, isLCP = false } = props;

  /** Only `compact` carries one — see the note on ListingCardProps. */
  const freshLabel = props.variant === 'compact' ? props.freshLabel ?? null : null;

  const isSpotlight = variant === 'spotlight';
  const isCarousel = variant === 'spotlight' || variant === 'compact';
  const isRow = variant === 'mobile-list';

  /**
   * SYND-15 — both kinds can be saved now, so the heart is unconditional and
   * the target carries the kind. The `canSave` gate that stood here was the
   * foreign key speaking; the foreign key is gone.
   */
  const saveTarget = savedTargetFromView(listing);

  const shell = `relative overflow-hidden rounded-xl border border-[#E7E6E6] bg-white ${CARD_HOVER}`;

  // ---------------------------------------------------------------------------
  // mobile-list — the design's `mobileList` card. VERTICAL on a phone: the image
  // spans the card, the facts sit under it.
  //
  // FIX ROUND 2. The first build made this a horizontal split — image left at
  // ~40%, text right — which is not what the design draws and is not the
  // Hemnet/Booli pattern the whole surface is modelled on. A 128px-wide
  // photograph of a house is a thumbnail, and it turned the highest-traffic
  // list in the product into a directory. Restored to the drawing.
  //
  // It stays horizontal from `sm` up, and only there: the Grid/List toggle
  // offers this variant on desktop, where a column of full-width vertical cards
  // at 1440 would be one card per screen. The design does not specify a desktop
  // list mode, so that half is a judgement call — but the phone half is not,
  // and `flex-col sm:flex-row` is what keeps the phone from ever inheriting it.
  // ---------------------------------------------------------------------------
  if (isRow) {
    return (
      <div className={`flex flex-col sm:flex-row ${shell}`}>
        <HomeLink
          to={listing.href}
          className={`absolute inset-0 z-[1] rounded-xl ${FOCUS_RING}`}
          aria-label={listing.title}
        >
          <span className="sr-only">{listing.title}</span>
        </HomeLink>

        <div className="relative h-[180px] w-full sm:h-[190px] sm:w-[260px] sm:flex-none">
          <CardPhoto listing={listing} sizes="(max-width: 639px) 100vw, 260px" isLCP={isLCP} />

          {/*
            The heart is INSIDE the image region in both directions, which is
            what puts it top-right of the photograph rather than top-right of a
            card whose photograph is only part of. 44×44 hit area, 30px visible
            circle — the design specifies that split literally, and it is why
            the control is reliably tappable.
          */}
          <SaveHeartButton
            saved={saved}
            onSave={(e) => onSave(saveTarget, e)}
            listingTitle={listing.title}
            /*
              The button is 44px around a 36px circle, so the circle already
              sits 4px inside it; `right-1 top-1` lands the visible mark ~8px
              from the corner while the hit area stays 44×44. The circle grew
              from the design's 30px on Dirie's instruction — see
              SaveHeartButton.
            */
            className="right-1 top-1 z-[2]"
          />

          <PhotoCountBadge count={listing.photoCount} className="bottom-2 right-2 z-[2]" />
        </div>

        {/* The design's 12px 14px 14px. */}
        <div className="min-w-0 flex-1 px-3.5 pb-3.5 pt-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[#8C8C86]">
            {listing.typeLabel}
          </div>
          <div className="mt-[3px] text-[14.5px] font-bold text-[#2D2D2D]">{listing.title}</div>
          {listing.areaLabel && (
            <div className="text-[12px] text-[#666666]">{listing.areaLabel}</div>
          )}
          <PriceBlock listing={listing} className="mt-[7px] text-[16px]" />
          <FactsBlock
            listing={listing}
            textClass="text-[12px]"
            gapClass="mt-[7px] gap-x-2.5"
            showTags
          />
          <AttributionLine listing={listing} className="mt-2 flex" />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // grid — All properties on desktop. Fills its cell rather than a fixed width.
  // ---------------------------------------------------------------------------
  if (variant === 'grid') {
    return (
      <div className={`flex h-full flex-col ${shell}`}>
        <HomeLink
          to={listing.href}
          className={`absolute inset-0 z-[1] rounded-xl ${FOCUS_RING}`}
          aria-label={listing.title}
        >
          <span className="sr-only">{listing.title}</span>
        </HomeLink>

        <div className="relative h-[220px]">
          <CardPhoto
            listing={listing}
            sizes="(max-width: 1023px) 100vw, 420px"
            isLCP={isLCP}
          />
          {/* The design's 10px inset from the corner: a 44px hit area around a
              36px circle leaves 4px, so the button sits 6px in and the VISIBLE
              circle lands at 10px. */}
          <SaveHeartButton
            saved={saved}
            onSave={(e) => onSave(saveTarget, e)}
            listingTitle={listing.title}
            className="right-1.5 top-1.5 z-[2]"
          />
          <PhotoCountBadge count={listing.photoCount} className="bottom-2.5 right-2.5 z-[2]" />
        </div>

        <div className="flex flex-1 flex-col px-4 pb-4 pt-3.5">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[#8C8C86]">
            {listing.typeLabel}
          </div>
          <div className="mb-0.5 mt-[3px] text-[15.5px] font-bold text-[#2D2D2D]">
            {listing.title}
          </div>
          {listing.areaLabel && (
            <div className="flex items-center gap-1 text-[12.5px] text-[#666666]">
              <PinIcon size={12} stroke="#666666" className="flex-none" />
              {listing.areaLabel}
            </div>
          )}
          <PriceBlock listing={listing} className="mt-[9px] text-[18px]" />
          <FactsBlock
            listing={listing}
            textClass="text-[12.5px]"
            gapClass="mt-[9px] gap-x-3"
            showTags
          />
          <AttributionLine listing={listing} className="mt-2.5 flex pt-2.5" />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // spotlight / compact — the carousels. Fixed card widths, per the design.
  // ---------------------------------------------------------------------------
  const cardWidth = isSpotlight ? 'w-[250px] lg:w-[312px]' : 'w-[250px] lg:w-[264px]';
  const imageHeight = isSpotlight ? 'h-[150px] lg:h-[196px]' : 'h-[150px] lg:h-[164px]';

  return (
    <div className={`flex-none snap-start ${cardWidth} ${shell}`}>
      <HomeLink
        to={listing.href}
        className={`absolute inset-0 z-[1] rounded-xl ${FOCUS_RING}`}
        aria-label={listing.title}
      >
        <span className="sr-only">{listing.title}</span>
      </HomeLink>

      <div className={`relative ${imageHeight}`}>
        <CardPhoto
          listing={listing}
          sizes="(max-width: 1023px) 250px, 312px"
          isLCP={isLCP}
        />

        {isSpotlight && listing.isSpotlight && (
          <SpotlightBadge className="pointer-events-none absolute left-2 top-2 z-[2] lg:left-2.5 lg:top-2.5" />
        )}
        {!isSpotlight && freshLabel && <FreshnessBadge label={freshLabel} />}

        <SaveHeartButton
          saved={saved}
          onSave={(e) => onSave(saveTarget, e)}
          listingTitle={listing.title}
          className="right-0.5 top-0.5 z-[2]"
        />

        {isSpotlight && (
          <PhotoCountBadge count={listing.photoCount} className="bottom-2 right-2 z-[2]" />
        )}
      </div>

      <div
        className={
          isSpotlight
            ? 'px-[13px] pb-[13px] pt-[11px] lg:px-4 lg:pb-[15px] lg:pt-3.5'
            : 'px-3.5 pb-3.5 pt-3'
        }
      >
        {/*
          The type kicker is desktop-only on the spotlight card — the design's
          250px mobile card drops it to keep title and price above the fold.
          It stays #8C8C86: it is a duplicate of information the card carries
          elsewhere (the facts row, the price shape), which is the one case the
          contrast rule allows for that grey.
        */}
        <div
          className={`text-[10.5px] font-bold uppercase tracking-[0.09em] text-[#8C8C86] ${
            isSpotlight ? 'hidden lg:block' : ''
          }`}
        >
          {listing.typeLabel}
        </div>

        <div
          className={`font-bold text-[#2D2D2D] ${
            isSpotlight ? 'text-[13.5px] lg:mb-0.5 lg:mt-[3px] lg:text-[15px]' : 'my-0.5 text-[14px]'
          }`}
        >
          {listing.title}
        </div>

        {listing.areaLabel &&
          (isSpotlight ? (
            <div className="mt-px flex items-center gap-1 text-[11.5px] text-[#666666] lg:text-[12.5px]">
              <PinIcon size={12} stroke="#666666" className="hidden flex-none lg:block" />
              {listing.areaLabel}
            </div>
          ) : (
            <div className="text-[12px] text-[#666666]">{listing.areaLabel}</div>
          ))}

        <PriceBlock
          listing={listing}
          className={isSpotlight ? 'mt-1.5 text-[15px] lg:mt-[9px] lg:text-[17px]' : 'mt-[7px] text-[15.5px]'}
        />

        <FactsBlock
          listing={listing}
          textClass={isSpotlight ? 'text-[11.5px] lg:text-[12.5px]' : 'text-[12px]'}
          gapClass={isSpotlight ? 'mt-1.5 gap-x-2.5 lg:mt-[9px] lg:gap-x-3' : 'mt-[7px] gap-x-2.5'}
          showTags={false}
        />

        {/*
          Attribution. Desktop-only on spotlight and absent on compact, per the
          design's own frames — EXCEPT on an indexed row, where naming the
          source is policy and cannot be dropped for a breakpoint.
        */}
        {isCarousel && listing.source === 'syndicated' ? (
          <AttributionLine listing={listing} className="mt-2.5 flex" />
        ) : (
          isSpotlight && <AttributionLine listing={listing} className="mt-2.5 hidden lg:flex" />
        )}
      </div>
    </div>
  );
};

export default ListingCard;
